import { Link, useLocation } from "wouter";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { usePageActions } from "@/contexts/PageActionsContext";
import { buildBreadcrumbs, getRouteConfig } from "@/lib/routeConfig";
import { Fragment } from "react";

export function TopNav() {
  const [location] = useLocation();
  const { actions, getActionHandler } = usePageActions();

  const breadcrumbs = buildBreadcrumbs(location);
  const routeConfig = getRouteConfig(location);

  // Filter visible actions
  const visibleActions = actions.filter((action) => action.visible !== false);

  return (
    <header
      className="flex items-center justify-between gap-4 px-4 py-2 border-b bg-white shrink-0"
      data-testid="top-nav"
    >
      <div className="flex items-center gap-3">
        <SidebarTrigger data-testid="button-sidebar-toggle" />

        {/* Icon from route config */}
        {routeConfig?.icon && (
          <div
            className="w-8 h-8 bg-primary rounded-none flex items-center justify-center"
            data-testid="top-nav-icon"
          >
            <routeConfig.icon className="h-4 w-4 text-primary-foreground" />
          </div>
        )}

        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <Breadcrumb data-testid="top-nav-breadcrumbs">
            <BreadcrumbList>
              {breadcrumbs.map((crumb, index) => (
                <Fragment key={crumb.path}>
                  {index > 0 && <BreadcrumbSeparator />}
                  <BreadcrumbItem>
                    {crumb.isCurrentPage ? (
                      <BreadcrumbPage data-testid={`breadcrumb-current-${crumb.label}`}>
                        {crumb.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link
                          href={crumb.path}
                          data-testid={`breadcrumb-link-${crumb.label}`}
                        >
                          {crumb.label}
                        </Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        )}
      </div>

      {/* Actions */}
      {visibleActions.length > 0 && (
        <div className="flex items-center gap-2" data-testid="top-nav-actions">
          {visibleActions.map((action) => (
            <Button
              key={action.id}
              variant={action.variant || "default"}
              onClick={() => {
                // Use getActionHandler to get the latest handler from the ref
                // This ensures we always call the most recent function reference
                const handler = getActionHandler(action.id);
                if (handler) handler();
              }}
              disabled={action.disabled}
              data-testid={action.testId || `action-${action.id}`}
            >
              {action.icon && <action.icon className="h-4 w-4 mr-2" />}
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </header>
  );
}
